import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { 
  Monitor, 
  Shield, 
  Activity, 
  Lock, 
  Eye, 
  Zap, 
  CheckCircle, 
  TrendingUp, 
  AlertTriangle,
  Server,
  FileText,
  BarChart3,
  Globe,
  Users,
  Clock,
  Bell
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

export default function AuthPage() {
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    const { error } = await signIn(email, password);
    
    if (error) {
      setError(error.message);
    } else {
      navigate("/dashboard");
    }
    
    setLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const tenantName = formData.get("tenantName") as string;
    const fullName = formData.get("fullName") as string;

    const { error } = await signUp(email, password, tenantName, fullName);
    
    if (error) {
      setError(error.message);
    } else {
      setError("");
      navigate("/dashboard");
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Hero Content */}
            <div className="text-white space-y-8">
              <div className="space-y-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-300 bg-clip-text text-transparent">
                    ServSpect
                  </h1>
                </div>
                
                <p className="text-xl lg:text-2xl text-blue-100 font-light leading-relaxed">
                  Enterprise-Grade Server Security & Monitoring Platform
                </p>
                
                <p className="text-lg text-slate-300 leading-relaxed">
                  Complete server monitoring and security management platform for Windows environments. 
                  Monitor performance metrics, analyze security logs, manage alerts, and automate 
                  vulnerability scanning - all from one powerful dashboard.
                </p>
              </div>

              {/* Key Features Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <BarChart3 className="w-6 h-6 text-green-400" />
                  <div>
                    <div className="font-semibold text-white">Real-time Metrics</div>
                    <div className="text-sm text-slate-300">Performance Monitoring</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <Server className="w-6 h-6 text-blue-400" />
                  <div>
                    <div className="font-semibold text-white">Server Management</div>
                    <div className="text-sm text-slate-300">Complete Infrastructure View</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <Bell className="w-6 h-6 text-purple-400" />
                  <div>
                    <div className="font-semibold text-white">Smart Alerts</div>
                    <div className="text-sm text-slate-300">Instant Notifications</div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <FileText className="w-6 h-6 text-yellow-400" />
                  <div>
                    <div className="font-semibold text-white">Windows Logs</div>
                    <div className="text-sm text-slate-300">Security Log Analysis</div>
                  </div>
                </div>
              </div>

              {/* Stats Row */}
              <div className="flex gap-8 pt-6 border-t border-white/20">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">99.9%</div>
                  <div className="text-sm text-slate-300">Uptime SLA</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">&lt;5s</div>
                  <div className="text-sm text-slate-300">Alert Response</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">24/7</div>
                  <div className="text-sm text-slate-300">Monitoring</div>
                </div>
              </div>
            </div>

            {/* Right Column - Auth Form */}
            <div className="lg:pl-8">
              <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0">
                <CardHeader className="text-center pb-6">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
                    <Monitor className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-slate-800">Start Your Free Trial</CardTitle>
                  <CardDescription className="text-slate-600">
                    Join thousands of companies protecting their infrastructure
                  </CardDescription>
                  
                  {/* Trust Badges */}
                  <div className="flex justify-center gap-2 mt-4">
                    <Badge variant="secondary" className="text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      SOC2 Certified
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <Shield className="w-3 h-3 mr-1" />
                      ISO27001
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <Lock className="w-3 h-3 mr-1" />
                      GDPR Compliant
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent>
                  <div className="w-full">
                    <form onSubmit={handleSignIn} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="signin-email" className="text-slate-700 font-medium">Email</Label>
                        <Input
                          id="signin-email"
                          name="email"
                          type="email"
                          placeholder="you@company.com"
                          className="h-11 border-slate-300 focus:border-blue-500"
                          required
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="signin-password" className="text-slate-700 font-medium">Password</Label>
                        <Input
                          id="signin-password"
                          name="password"
                          type="password"
                          className="h-11 border-slate-300 focus:border-blue-500"
                          required
                        />
                      </div>
                      
                      {error && (
                        <Alert variant="destructive" className="border-red-200">
                          <AlertTriangle className="h-4 w-4" />
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}
                      
                      <Button 
                        type="submit" 
                        className="w-full h-12 bg-slate-800 hover:bg-slate-900 text-white font-semibold" 
                        disabled={loading}
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Signing in...
                          </div>
                        ) : (
                          "Sign In to Dashboard"
                        )}
                      </Button>
                    </form>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div className="bg-slate-800 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Enterprise-Grade Security Architecture
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              Onze multi-layered beveiligingsarchitectuur beschermt uw kritieke server data en metrics 
              met industry-leading standaarden en zero-trust principes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Data Transport Security */}
            <div className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-xl border border-slate-600/50">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Secure Data Transport</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  TLS 1.3 versleuteling voor alle data
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Device tokens met SHA-256 hashing
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Anti-replay bescherming met nonces
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  HMAC signature validatie
                </li>
              </ul>
            </div>

            {/* Access Control */}
            <div className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-xl border border-slate-600/50">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Zero-Trust Access Control</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Row Level Security (RLS) policies
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Multi-tenant data isolatie
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  Role-based toegangscontrole
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-blue-400" />
                  MFA enforcement per tenant
                </li>
              </ul>
            </div>

            {/* Threat Protection */}
            <div className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-xl border border-slate-600/50">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-600 rounded-lg flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Threat Detection & Prevention</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  Rate limiting & DoS bescherming
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  SQL injection & XSS preventie
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  Device blacklisting systeem
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-red-400" />
                  Real-time security event monitoring
                </li>
              </ul>
            </div>

            {/* Audit & Compliance */}
            <div className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-xl border border-slate-600/50">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-600 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Audit & Compliance</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Comprehensive audit logging
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Risk-based event classification
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Data retention policies
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-purple-400" />
                  Security event monitoring & alerts
                </li>
              </ul>
            </div>

            {/* Data Protection */}
            <div className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-xl border border-slate-600/50">
              <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Data Protection & Privacy</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  End-to-end data versleuteling
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  GDPR compliance & privacy controls
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  Automatic data retention management
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-yellow-400" />
                  PII detection & masking
                </li>
              </ul>
            </div>

            {/* Infrastructure Security */}
            <div className="bg-slate-700/50 backdrop-blur-sm p-6 rounded-xl border border-slate-600/50">
              <div className="w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">Infrastructure Security</h3>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-400" />
                  Secure cloud hosting (Supabase)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-400" />
                  99.9% uptime SLA garantie
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-400" />
                  Automated backup & recovery
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-400" />
                  Geographic data replication
                </li>
              </ul>
            </div>
          </div>

          {/* Security Standards */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-6 p-6 bg-slate-700/30 rounded-xl backdrop-blur-sm border border-slate-600/30">
              <div className="text-center">
                <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <div className="text-sm font-semibold text-white">TLS 1.3</div>
                <div className="text-xs text-slate-400">Encryption</div>
              </div>
              <div className="text-center">
                <Lock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <div className="text-sm font-semibold text-white">SHA-256</div>
                <div className="text-xs text-slate-400">Token Hashing</div>
              </div>
              <div className="text-center">
                <Eye className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <div className="text-sm font-semibold text-white">Zero Trust</div>
                <div className="text-xs text-slate-400">Architecture</div>
              </div>
              <div className="text-center">
                <CheckCircle className="w-8 h-8 text-teal-400 mx-auto mb-2" />
                <div className="text-sm font-semibold text-white">OWASP</div>
                <div className="text-xs text-slate-400">Secure Coding</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-slate-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              Complete Server Management & Security Platform
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Monitor performance, analyze security logs, manage alerts, and automate vulnerability scans. 
              Everything you need to keep your Windows server infrastructure secure and running smoothly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Cards */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Real-Time Metrics</h3>
              <p className="text-slate-600 leading-relaxed">
                Monitor CPU, memory, disk usage, and network performance across all your servers. 
                Get instant visibility into system health with detailed performance dashboards and historical trends.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>CPU, Memory & Disk monitoring</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Network performance tracking</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Historical trend analysis</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-6">
                <Server className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Server Management</h3>
              <p className="text-slate-600 leading-relaxed">
                Complete overview of your server infrastructure with real-time status monitoring, 
                uptime tracking, and centralized management of all your Windows servers.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Centralized server dashboard</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Real-time status monitoring</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Uptime & availability tracking</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-6">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Smart Alerts</h3>
              <p className="text-slate-600 leading-relaxed">
                Intelligent alerting system that notifies you immediately when performance thresholds 
                are exceeded or security events occur. Configurable alerts for all critical metrics.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Performance threshold alerts</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Security event notifications</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Email & Slack integration</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-6">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Windows Log Analysis</h3>
              <p className="text-slate-600 leading-relaxed">
                Comprehensive Windows event log collection and analysis. Monitor security events, 
                system errors, and application logs with advanced filtering and search capabilities.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Security event monitoring</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>System & application logs</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Advanced log filtering</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center mb-6">
                <Eye className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Security Scanners</h3>
              <p className="text-slate-600 leading-relaxed">
                Automated security scanning tools to identify vulnerabilities, misconfigurations, 
                and potential security risks across your server infrastructure.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Vulnerability scanning</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Security misconfiguration detection</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Automated security reports</span>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg border border-slate-200 hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-lg flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-3">Task Management</h3>
              <p className="text-slate-600 leading-relaxed">
                Organize and track security tasks, maintenance activities, and remediation efforts. 
                Keep your team aligned with automated task assignment and progress tracking.
              </p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Security task tracking</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Maintenance scheduling</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Team collaboration tools</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Monitor Your Server Infrastructure?
          </h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Join companies that rely on ServSpect to monitor performance, analyze security logs, 
            and maintain their Windows server infrastructure. Start your free trial today.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>30-day free trial</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2 text-slate-300">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span>Cancel anytime</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}